<template>
    <main class="p-2 md:p-5 w-full h-full max-h-main flex flex-col items-start min-h-main">
        <div class="w-full flex flex-col gap-2">
            <app-filter ref="filter" v-model="filter"></app-filter>
        </div>
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th style="width: 5rem" class="text-left border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('name')"
                            >
                                {{ $t('commons.name') }}
                                <div v-if="sortKey === 'name'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th style="width: 5rem" class="text-left border-r p-1 bg-white">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('siret')"
                            >
                                <span>{{ $t('organizations.siret') }}</span>
                                <div v-if="sortKey === 'siret'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th style="width: 5rem" class="text-left border-r p-1 bg-white">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('city')"
                            >
                                <span>{{ $t('organizations.city') }}</span>
                                <div v-if="sortKey === 'city'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th style="width: 5rem" class="text-left border-r p-1 bg-white">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('endDate')"
                            >
                                <span>{{ $t('organizations.owner') }}</span>
                                <div v-if="sortKey === 'owner'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th style="width: 5rem" class="text-left border-r p-1 bg-white">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('type')"
                            >
                                <span>{{ $t('organizations.type') }}</span>
                                <div v-if="sortKey === 'type'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="organization in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="border-r p-1 underline">
                                <router-link
                                    :to="{
                                        name: 'organization',
                                        params: {
                                            organizationId: organization.id,
                                        },
                                    }"
                                >
                                    {{ organization.name }}
                                </router-link>
                            </td>
                            <td class="border-r p-1">
                                {{ organization.siret }}
                            </td>
                            <td class="border-r p-1">
                                {{ organization.city }}
                            </td>
                            <td class="border-r p-1">
                                <div class="flex flex-col">
                                    <div>{{ organization.owner.firstName }} {{ organization.owner.lastName }}</div>
                                    <div>
                                        <app-phone :phone="organization.owner.phone" />
                                        <app-mail :email="organization.owner.email" />
                                    </div>
                                </div>
                            </td>
                            <td class="border-r p-1">
                                {{ organization.subscriptions.find(({ type }) => type === 'PRO') ? 'PRO' : 'STARTER' }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </main>
</template>

<script>
import { filterMatch, sortBy } from '@/services/sanitize.service';
import AppButton from '@/components/appButton/AppButton';
import AppFilter from '@/components/appFilter/AppFilter.vue';
import { getOrganizations } from '@/features/organizations/organizations.service';
import AppPhone from '@/components/appPhone/AppPhone.vue';
import AppMail from '@/components/appMail/AppMail.vue';

export default {
    components: {
        AppMail,
        AppPhone,
        AppFilter,
        AppButton,
    },
    async created() {
        this.restoreFilter();
        this.organizations = await getOrganizations().map((item) => ({ ...item, type: 'STARTER' }));
        this.loading = false;
    },
    computed: {
        filteredItems() {
            let result = this.filterFn(this.filter);
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    if (this.sortKey === 'name') {
                        return item.name;
                    } else if (this.sortKey === 'siret') {
                        return item.siret;
                    } else if (this.sortKey === 'city') {
                        return item.city;
                    } else if (this.sortKey === 'owner') {
                        return item.owner.lastName;
                    } else if (this.sortKey === 'type') {
                        return item.type;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
    },
    methods: {
        sortBy(key) {
            if (key === this.sortKey) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
        },
        saveFilter() {
            localStorage.setItem(
                'organizations_filter',
                JSON.stringify({
                    filter: this.filter,
                    sortAsc: this.sortAsc,
                    sortKey: this.sortKey,
                }),
            );
        },
        restoreFilter() {
            const cache = localStorage.getItem('organizations_filter');
            if (cache) {
                const parsedCache = JSON.parse(cache);
                this.sortAsc = !!parsedCache.sortAsc;
                this.sortKey = parsedCache.sortKey || 'name';
                this.filter = parsedCache.filter || '';
            }
        },
        filterOrganizations(item) {
            return (
                item.siret +
                item.name +
                item.city +
                item.owner.email +
                item.owner.lastName +
                item.type +
                item.owner.email
            );
        },
        filterFn(filter) {
            this.saveFilter();
            return this.organizations.filter((item) => {
                const filterString = [
                    item.name,
                    item.city,
                    item.owner.email,
                    item.owner.lastName,
                    item.siret,
                    item.type,
                ].join();
                return (
                    filterMatch(filterString, filter, true) &&
                    (!item.validatedAt || this.active) &&
                    (!!item.validatedAt || this.waiting)
                );
            });
        },
        newOrganization() {
            this.$refs.newOrganization.open();
        },
    },
    data() {
        return {
            filter: '',
            sortKey: 'name',
            sortAsc: false,
            loading: true,
            readOnly: true,
            organizations: [],
        };
    },
};
</script>
